/**
 * Centralized error handler for analytics tracking.
 * Reports errors to backend for monitoring and debugging.
 */

interface AnalyticsErrorData {
  error_type: string;
  error_message: string;
  error_stack?: string;
  error_source: 'frontend';
  session_id?: string;
  anonymous_id?: string;
  event_type?: string;
  event_data?: Record<string, unknown>;
  page_url?: string;
}

const ANONYMOUS_ID_KEY = 'ero_anonymous_id';
const SESSION_ID_KEY = '__analytics_session_id__';

/**
 * Report an analytics error to the backend for monitoring
 */
export async function reportAnalyticsError(
  errorType: string,
  error: Error | string,
  context?: {
    eventType?: string;
    eventData?: Record<string, unknown>;
  }
): Promise<void> {
  try {
    const errorMessage = error instanceof Error ? error.message : error;
    const errorStack = error instanceof Error ? error.stack : undefined;

    const payload: AnalyticsErrorData = {
      error_type: errorType,
      error_message: errorMessage,
      error_stack: errorStack,
      error_source: 'frontend',
      session_id: sessionStorage.getItem(SESSION_ID_KEY) || undefined,
      anonymous_id: localStorage.getItem(ANONYMOUS_ID_KEY) || undefined,
      event_type: context?.eventType,
      event_data: context?.eventData,
      page_url: window.location.pathname,
    };

    // Use fetch directly to avoid circular dependencies
    await fetch('/api/v1/analytics/errors/log', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch {
    // Double-silent: don't log errors about error logging
    // But at least try console in development
    if (import.meta.env.DEV) {
      console.warn('[Analytics Error Handler] Failed to report error:', errorType, error);
    }
  }
}

/**
 * Wrap an async function with error reporting
 */
export function withAnalyticsErrorReporting<T extends (...args: unknown[]) => Promise<unknown>>(
  fn: T,
  errorType: string
): T {
  return (async (...args: Parameters<T>) => {
    try {
      return await fn(...args);
    } catch (error) {
      void reportAnalyticsError(errorType, error instanceof Error ? error : String(error));
      throw error;
    }
  }) as T;
}

/**
 * Safe wrapper for analytics operations that should never throw
 */
export async function safeAnalyticsCall<T>(
  operation: () => Promise<T>,
  errorType: string,
  context?: { eventType?: string; eventData?: Record<string, unknown> }
): Promise<T | null> {
  try {
    return await operation();
  } catch (error) {
    void reportAnalyticsError(
      errorType,
      error instanceof Error ? error : String(error),
      context
    );
    return null;
  }
}

export default {
  reportAnalyticsError,
  withAnalyticsErrorReporting,
  safeAnalyticsCall,
};
